/**
 * Russian message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: вы, lowercase (locked in l10n/locales.md).
 *
 * Platform terms follow Russian UI conventions (настройки, сопряжение
 * for pairing, дублирование for notification mirroring, удалить for
 * dismiss, беседа for conversation). "Glance" is the untranslated
 * feature brand name (glossary D1); «клип» renders the D2 loanword in
 * Cyrillic (locked CJK/Cyrillic clarification).
 *
 * Plural notes: Russian is a {one, few, many, other} CLDR locale —
 * every count-bearing `selectPlural("ru", …)` supplies all four forms.
 * `one` covers 21, 31, 41… (not just 1), so numbers are never hardcoded
 * in the `one` form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Подключено",
    statusReconnecting: "Переподключение…",
    statusDisconnected: "Отключено",
    openSettings: "Открыть настройки",
    pairedFallbackDevice: "ваш телефон",
    publicModeToggleEnable: "Скрыть содержимое уведомлений",
    publicModeToggleDisable: "Показать содержимое уведомлений",
  },

  notifications: {
    replyAriaLabel: "Ответить",
    replyPlaceholder: "Ответить…",
    replySend: "Отправить",
    sent: "Отправлено",
    failedToSend: "Не удалось отправить",
    unread: "Непрочитано",
    dismiss: "Удалить",
    notificationImageAlt: "Изображение из уведомления",
  },

  conversations: {
    replyAriaLabel: "Ответить",
    replyPlaceholder: "Ответить…",
    replySend: "Отправить",
    sent: "Отправлено",
    failedToSend: "Не удалось отправить",
    dismiss: "Удалить беседу",
    messagesListLabel: "Сообщения в этой беседе",
    deviceOwner: "Вы",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Нет непрочитанных сообщений"
        : `${count} ${selectPlural("ru", count, {
            one: "непрочитанное сообщение",
            few: "непрочитанных сообщения",
            many: "непрочитанных сообщений",
            other: "непрочитанного сообщения",
          })}`,
  },

  list: {
    today: "Сегодня",
    yesterday: "Вчера",
    searchNotifications: "Поиск уведомлений",
    clearSearch: "Очистить поиск",
    clearAll: "Удалить все",
    markAllRead: "Отметить все как прочитанные",
    groupCheckboxLabel: "Группировать",
    noNotificationsTitle: "Уведомлений пока нет",
    noNotificationsDesc: "Новые уведомления, продублированные с телефона, появятся здесь.",
    noMatchesTitle: "Ничего не найдено",
    notificationsList: "Уведомления",
    privacyAppList: "Уведомления по приложениям",
    clear: "Удалить",
    clearMatches: (count: number): string =>
      `Удалить ${count} ${selectPlural("ru", count, {
        one: "совпадение",
        few: "совпадения",
        many: "совпадений",
        other: "совпадения",
      })}`,
    noMatchesDesc: (query: string): string => `Нет уведомлений по запросу «${query}».`,
    appNotificationsGroup: (appName: string): string => `Уведомления ${appName}`,
    dismissAllFrom: (appName: string): string => `Удалить все от ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("ru", count, {
              one: "сообщение",
              few: "сообщения",
              many: "сообщений",
              other: "сообщения",
            })
          : kind === "calls"
            ? selectPlural("ru", count, {
                one: "звонок",
                few: "звонка",
                many: "звонков",
                other: "звонка",
              })
            : selectPlural("ru", count, {
                one: "уведомление",
                few: "уведомления",
                many: "уведомлений",
                other: "уведомления",
              });
      return `${count} ${noun}`;
    },
    kbdDismiss: "удалить",
    kbdNavigate: "навигация",
    kbdOrSeparator: "или",
  },

  settings: {
    back: "Назад к уведомлениям",
    settings: "Настройки",
    general: "Общие",
    theme: "Тема",
    themeAuto: "Автоматическая",
    themeLight: "Светлая",
    themeDark: "Тёмная",
    language: "Язык",
    languageSystemDefault: "Как в системе",
    notificationsSection: "Уведомления",
    osNotifications: "Системные уведомления",
    callNotifications: "Уведомления о звонках",
    privacySecurity: "Конфиденциальность",
    analyticsToggle: "Анонимная статистика использования",
    analyticsToggleDescription: "Только счётчики и вехи — никогда ваши уведомления.",
    statusConnected: "Подключено",
    statusReconnecting: "Переподключение…",
    statusDisconnected: "Отключено",
    disconnectFromPhone: "Отключить от телефона",
    disconnectConfirm: "Вы уверены? Сопряжение с устройством будет разорвано.",
    yesDisconnect: "Да, отключить",
    cancel: "Отмена",
    version: "Версия",
    encryption: "Шифрование",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Нужна помощь? Напишите на __EMAIL__",
    disconnectFailed: "Не удалось отключить — попробуйте ещё раз",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Отключено, но не удалось очистить локальные данные: ${errorMsg}. Перезагрузите страницу.`,
    disconnectLocalCleanupFailed:
      "Отключено, но не удалось очистить локальные данные. Перезагрузите страницу.",
  },

  pairing: {
    enterCodeInstruction: "Введите этот код в приложении Reflecto",
    pairedSuccessfully: "Сопряжение выполнено!",
    pairingFailed: "Сопряжение не удалось",
    requestingPairingCode: "Запрашиваем код сопряжения…",
    codeExpired: "Срок действия кода истёк",
    tryAgain: "Попробовать ещё раз",
    failedToStartPairing: "Не удалось начать сопряжение",
    installHeading: "Ещё нет приложения?",
    installScanHint: "Отсканируйте камерой телефона",
    installQrLabel: "QR-код для установки Reflecto на телефон",
    capabilitiesHeading: "После сопряжения вы сможете:",
    capabilityMirror: "Видеть уведомления телефона здесь",
    capabilityReply: "Отвечать, не беря телефон в руки",
    capabilityShare: "Обмениваться буфером обмена и файлами в обе стороны",
    encryptionNote: "Сквозное шифрование. Сервер никогда не видит ваш контент.",
  },

  dnd: {
    title: "Не беспокоить",
    description: "Приостановить уведомления на этом устройстве на ближайший час.",
    snooze1h: "Отложить на 1 час",
    snoozeActiveUntil: (formattedTime: string): string => `Активно до ${formattedTime}`,
    cancelSnooze: "Отмена",
  },

  connectors: {
    title: "Подключённые сервисы",
    emptyBody:
      "Подключённых сервисов пока нет. Подключите ChatGPT или Claude в настройках их коннекторов, затем подтвердите код на телефоне.",
    scopeSend: "Может отправлять уведомления",
    added: (date: string): string => `Добавлен ${date}`,
    lastUsed: (relative: string): string => `Последнее использование ${relative}`,
    neverUsed: "Не использовался",
    revoke: "Отключить",
    revokeConfirm: (label: string): string =>
      `Отключить ${label}? Доступ будет потерян немедленно.`,
    revokeAction: "Отключить",
    revokeConfirmLabel: (label: string): string => `Отключить ${label}`,
    cancel: "Отмена",
    loadError: "Не удалось загрузить подключённые сервисы. Попробуйте ещё раз.",
    revokeError: "Не удалось отключить сервис. Попробуйте ещё раз.",
    retry: "Повторить",
  },

  safetyNumber: {
    rowLabel: "Проверить шифрование",
    rowSublabel: "Сравните коды безопасности с телефоном",
    cardTitle: "Код безопасности",
    caption: (deviceLabel: string): string =>
      `Сравните с числом, показанным на ${deviceLabel}. Если оба числа совпадают, ваше сквозное шифрование целостно.`,
    repairNote: "Этот номер изменяется при повторном сопряжении устройства.",
    copy: "Копировать",
    copied: "Скопировано",
    maskedHint: "Скрыто, пока включен режим скрытия содержимого",
  },

  feedback: {
    title: "Отправить отзыв",
    close: "Закрыть",
    messageLabel: "Ваше сообщение",
    messagePlaceholder: "Нашли баг, есть идея или что-то не так? Расскажите нам.",
    categoryLabel: "Категория",
    categoryBug: "Баг",
    categoryIdea: "Идея",
    categoryOther: "Другое",
    emailLabel: "Эл. почта (необязательно, только если хотите получить ответ)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Мы не добавим вас ни в какие рассылки.",
    disclosure:
      "Ваше сообщение отправляется на сервер Reflecto и сохраняется в нашем внутреннем трекере задач. Мы прикладываем версию приложения и идентификатор устройства, присвоенный при сопряжении, чтобы сопоставлять отчёты. Мы не видим ваши уведомления, контакты и другие личные данные. Эл. почта необязательна — укажите её, только если хотите получить ответ. Мы не добавим вас ни в какие рассылки.",
    submit: "Отправить отзыв",
    sending: "Отправляем…",
    success: "Спасибо — отзыв прочитает человек, обычно в течение 24 часов",
    error: "Не удалось отправить. Попробуйте ещё раз.",
    done: "Готово",
    sendFeedback: "Отправить отзыв",
  },

  uninstall: {
    headline: "Расширение Reflecto удалено",
    body: "Учётной записи никогда не было, а расширение хранило сопряжение только на этом компьютере, так что убирать за ним нечего.",
    ask: "Если у вас есть тридцать секунд, расскажите, что пошло не так.",
    categoryLegend: "Какого рода этот отзыв?",
    categoryBug: "Что-то сломалось",
    categoryIdea: "Не хватало нужной мне функции",
    categoryOther: "Что-то другое",
    messageLabel: "Что пошло не так?",
    messagePlaceholder: "Через несколько часов уведомления перестали приходить.",
    messageRequired: "Сначала напишите предложение, затем отправьте.",
    charactersLeft: (count: number): string => `Осталось ${count} символов`,
    emailLabel: "Эл. почта (необязательно)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Используется только для ответа вам. Больше ни для чего.",
    submit: "Отправить отзыв",
    sending: "Отправляем…",
    retry: "Попробовать ещё раз",
    privacyNote:
      "Эта форма не связана ни с одним устройством. В ней нет ваших уведомлений, буфера обмена и файлов. Эл. почта необязательна и нужна только для ответа.",
    success: "Спасибо. Каждый такой отзыв читает человек.",
    errorGeneric: "Отправить не удалось. Ваше сообщение осталось здесь.",
    errorRateLimited:
      "Вы недавно отправили несколько сообщений. Попробуйте примерно через час. Ваш текст останется здесь.",
    footerWebApp: "Веб-приложение Reflecto работает и без расширения.",
    footerWebAppLink: "Открыть веб-приложение",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Закрыть",
    aiSummaryTag: "Сводка ИИ",
    errorAi: "Сейчас не удалось составить сводку.",
    retryCta: "Попробовать ещё раз",
    downloadHeading: "Сводки на базе ИИ",
    downloadBody:
      "Догоняйте пропущенное со сводкой от ИИ прямо на устройстве. Всё работает только на вашем устройстве.",
    downloadCta: "Скачать модель",
    downloadPrivacyNote: "Одноразовая загрузка. Сервер никогда не видит ваши уведомления.",
    downloadingHeading: "Скачиваем модель…",
    caughtUp: "Вы всё просмотрели — сейчас ничего не требует вашего внимания.",
    needsYou: "Требует внимания",
    quiet: "Остальное",
    scanning: (pending: number): string => `Готовим сводку: ${pending}…`,
    jump: "Открыть уведомление",
    affordanceReply: "Ответить",
    affordanceOpen: "Открыть",
    affordanceCopyOtp: "Копировать код",
    buttonLabel: "Glance",
  },

  update: {
    title: "Доступно обновление",
    description: "Готова новая версия Reflecto. Перезагрузите страницу, чтобы обновиться.",
    reload: "Перезагрузить",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Мы назвали это устройство «${name}».`,
    cardLooksGood: "Подходит",
    cardEdit: "Изменить",
    cardAriaLabel: "Подтвердите имя устройства",
    renameTitle: "Переименовать это устройство",
    renameLabel: "Имя устройства",
    renameSave: "Сохранить",
    renameCancel: "Отмена",
    renameFailed: "Не удалось переименовать. Попробуйте ещё раз.",
  },

  collision: {
    title: (name: string): string => `У вас уже есть «${name}»`,
    subtitle: "Как назвать это устройство?",
    chipWork: (name: string): string => `Рабочий ${name}`,
    chipHome: (name: string): string => `Домашний ${name}`,
    chipPersonal: (name: string): string => `Личный ${name}`,
    customLabel: "Своё имя",
    save: "Сохранить",
    useAnyway: "Всё равно использовать исходное имя",
    saveFailed: "Не удалось сохранить новое имя. Попробуйте ещё раз.",
    ariaLabel: "Устраните совпадение имён устройств",
  },

  clipboard: {
    title: "Буфер обмена",
    emptyTitle: "Пока нет клипов",
    empty: "Клипы с других ваших устройств появляются здесь. Нажмите, чтобы скопировать.",
    from: (label: string): string => `от ${label}`,
    to: (label: string): string => `на ${label}`,
    copy: "Копировать в буфер обмена",
    copied: "Скопировано",
    reveal: "Показать",
    hide: "Скрыть",
    sensitiveChip: "Конфиденциально",
    cloudChip: "через ваш токен API",
    maskedHint: "Содержимое скрыто — нажмите «Показать»",
    delete: "Удалить клип",
    sendPlaceholder: "Вставьте текст для отправки…",
    sendCta: "Отправить",
    sendSensitiveToggle: "Отметить как конфиденциальный",
    sendSuccess: (n: number): string =>
      `Отправлено на ${n} ${selectPlural("ru", n, {
        one: "устройство",
        few: "устройства",
        many: "устройств",
        other: "устройства",
      })}.`,
    sendPartial: (n: number, total: number): string =>
      `Отправлено на ${n} из ${total} ${selectPlural("ru", total, {
        one: "устройства",
        few: "устройств",
        many: "устройств",
        other: "устройства",
      })}.`,
    sendNoDevices: "Нет сопряжённых устройств для отправки.",
    sendFailed: "Не удалось отправить. Попробуйте ещё раз.",
    sendEmpty: "Нечего отправлять.",
    dockRecentHeading: "Недавние",
    dockEmptyClipboard: "Буфер обмена пуст.",
    clearAll: "Удалить все",
    clearAllConfirm: "Да, удалить все",
    clearAllCancel: "Отмена",
    shareTitle: "Отправить на ваши устройства",
    shareReview:
      "Проверьте, что вы отправляете. Данные уйдут на ваши сопряжённые устройства со сквозным шифрованием.",
    shareSend: "Отправить",
    shareCancel: "Отмена",
    shareNotPaired: "Чтобы отправить общий текст, сначала выполните сопряжение с устройством.",
    shareOpenApp: "Открыть Reflecto",
    pasteFromClipboard: "Вставить из буфера обмена",
    readFailed: "Не удалось прочитать буфер обмена — вставьте текст ниже.",
  },
  install: {
    title: "Установить Reflecto",
    body: "Добавьте на главный экран для быстрого доступа.",
    cta: "Установить",
    dismissAria: "Закрыть предложение об установке",
  },
  nav: {
    home: "Главная",
    clipboard: "Буфер обмена",
    files: "Файлы",
    ariaLabel: "Основная навигация",
    fileHiddenPublicMode: "Скрыто, пока включён приватный режим.",
  },
  common: {
    loading: "Загрузка",
  },
  errors: {
    notPaired: "Не сопряжено",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} на ${os}`,
    webFallback: "Веб-браузер",
  },
  palette: {
    placeholder: "Введите команду…",
    noResults: "Команды не найдены",
    ariaLabel: "Палитра команд",
    clearAll: "Удалить все уведомления",
    glance: "Сводка Glance",
    cancelSnooze: "Отменить отсрочку",
    copyClip: "Копировать последний полученный клип",
  },

  fileTransfer: {
    sendTitle: "Отправить файл",
    sendChooseFile: "Выбрать файл",
    sendDeviceLabel: "Отправить на",
    sendPhoneFallbackName: "Телефон",
    send: "Отправить файл",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Подготовка…" : `Подготовка — ${pct}%`,
    sendDelivering: "Доставка на телефон…",
    sendWaitingAccept: "Ожидание подтверждения на телефоне…",
    sendWaitingAppOpen: "Откройте Reflecto на телефоне, чтобы принять",
    sendWaitingAppOpenHint:
      "Телефон не может получать push-уведомления, поэтому покажет этот файл только после открытия Reflecto.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Загрузка части ${partNumber} из ${partCount}…`,
    sendDone: "Отправлено.",
    sendFailedRejected: "Телефон отклонил файл.",
    sendFailedNoAnswer: "Телефон не ответил.",
    sendFailedUploadFailed: "Не удалось загрузить. Попробуйте ещё раз.",
    sendFailedCancelled: "Отменено.",
    sendFailedGeneric: "Что-то пошло не так. Попробуйте ещё раз.",
    sendTooLarge: (maxMiB: number): string => `Файл слишком большой (макс. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Не закрывайте эту вкладку, пока передача не завершится.",
    sendAnother: "Отправить ещё",
    sendNoDevice: "Чтобы отправлять файлы, сначала выполните сопряжение с телефоном.",
    sendCancel: "Отмена",
    sendPeerProgress: (chunksDone: number): string => `Телефон получил фрагментов: ${chunksDone}.`,

    receiveTitle: "Входящие файлы",
    receiveOfferFrom: (sender: string): string => `${sender} хочет отправить вам файл`,
    receiveAccept: "Принять",
    receiveDecline: "Отклонить",
    receiveReceiving: (n: number, total: number): string => `Получение части ${n} из ${total}…`,
    receiveDone: "Сохранено.",
    receiveFailed: "Передача не удалась.",
    receiveCancelled: "Отменено.",
    receiveCancel: "Отмена",

    errorChunkAuthFail: "Передача не удалась — не удалось проверить данные. Попробуйте ещё раз.",
    errorHashMismatch:
      "Передача не удалась — файл не прошёл проверку целостности. Попробуйте ещё раз.",
    errorStorageFull: "Недостаточно места в хранилище для этой передачи.",
    errorExpired: "Срок действия передачи истёк.",
    errorQuotaExceeded: "Достигнут дневной лимит передач — попробуйте снова завтра.",
    errorInternal: "Передача не удалась — что-то пошло не так.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct} %`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Закрыть",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Ход передачи",
    outgoing: (n: number): string => `Исходящие (${n})`,
    incoming: (n: number): string => `Входящие (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Принять все",
    dropTitle: "Перетащите сюда",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Файлы до ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Перетащите сюда или выберите файл для отправки",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Напрямую",
    transportDirectHint:
      "Отправлено напрямую между устройствами — никогда не хранится на наших серверах.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Через сервер",
    transportRelayHint:
      "Передано через наши серверы, по-прежнему со сквозным шифрованием — мы не можем это прочитать.",
    directBadge: "Напрямую",
  },
  time: {
    justNow: "только что",
    minutesAgo: (count: number): string => `${count} мин. назад`,
    hoursAgo: (count: number): string => `${count} ч. назад`,
  },
} satisfies MessagesShape;
